import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LanguagePipe } from '../pipe/language.pipe';
import { describeServerError, parseServerError } from '../service-data/http-error';

/**
 * One place to put a failed server call in front of the user.
 *
 * ---------------------------------------------------------------------------
 * WHY (PF-13)
 * ---------------------------------------------------------------------------
 * Every failure path in the order / replenishment flow was a bare
 * `console.log`. From the operator's seat a rejected replenishment and a
 * successful one looked identical: the spinner stopped and nothing else
 * happened. The order simply did not exist, and nobody found out until someone
 * went looking for stock that was never made.
 *
 * `AlertService` was not the right vehicle for these. It renders a stacked
 * banner that is cleared on the next route change and can sit above the fold
 * on the long manufacturing screens, so a message can be posted and never
 * seen. A snack-bar is an overlay -- it appears wherever the user is looking,
 * on any screen, and auto-dismisses. This is the same choice `CartPageComponent`
 * and `RateConfigEditorComponent` already made locally; this service just stops
 * it being copy-pasted a fourth and fifth time.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT SHOWS
 * ---------------------------------------------------------------------------
 * The server's own sentence (`SmartError.details`) when there is one, because
 * it names the actual row and state -- "ManufacturingSet 812 is CLOSED and is
 * no longer accepting items" is worth more than any generic string we could
 * write. Otherwise the caller's already-translated fallback.
 *
 * It deliberately never shows `SmartError.message`: that is a stable machine
 * code (`request error`, `not found`, `manufacturing set closed`) meant for
 * branching. Displaying it produces an alert reading "request error", which
 * reads as a bug in our UI rather than a rejection of the operator's input.
 * See `docs/contracts/inbox/2026-08-27-folder-forensics-and-error-shape-correction.md` §3.
 */
@Injectable()
export class ServerErrorToastService {

  constructor(private snackBar: MatSnackBar) {}

  /**
   * Report a failed server call.
   *
   * @param operation short identifier for the console log, e.g. `addOrderDeliverTo`
   * @param error     whatever the observable errored with
   * @param fallback  translated sentence to use when the server sent no prose
   */
  reportError(operation: string, error: any, fallback: string): void {
    this.logFailure(operation, error);
    this.show(describeServerError(error, fallback), 8000, 'server-snack-error');
  }

  /**
   * Report a failed server call showing **our** already-translated sentence,
   * never the server's.
   *
   * ---------------------------------------------------------------------------
   * WHY THIS EXISTS ALONGSIDE `reportError`
   * ---------------------------------------------------------------------------
   * `reportError` prefers `SmartError.details` because it names the actual row
   * and state, and on an operator-facing manufacturing screen that specificity
   * is worth more than a generic sentence. But `details` is **English in all
   * 21 locale bundles** -- the server sends no translations and never will --
   * so preferring it means a French or Arabic reader gets English, and the
   * translated string the caller already prepared is silently discarded.
   *
   * House rule (2026-10-23): every string presented to an end user is in their
   * language. The exception is data from their own account -- a book title, a
   * store domain, an organization name -- which is shown verbatim because it
   * is theirs, not ours.
   *
   * So: use this on any surface whose reader may not be an English speaker,
   * and `reportError` where naming the exact row matters more. The server's
   * sentence is **not lost** either way -- it goes to the console with the
   * status and the machine code, which is what support actually needs.
   *
   * @param message already translated, e.g. `LanguagePipe.get('...')`
   */
  reportLocalizedError(operation: string, error: any, message: string): void {
    this.logFailure(operation, error);
    this.show(message, 8000, 'server-snack-error');
  }

  /** Console record of a failure. Identical for both report paths. */
  private logFailure(operation: string, error: any): void {
    const info = parseServerError(error);
    console.error(`${operation} failed`, {
      httpStatus: info.httpStatus,
      statusCode: info.statusCode,
      code: info.code,
      details: info.details,
      error: error
    });
  }

  /** Report a problem we detected ourselves (already-translated text). */
  reportMessage(message: string): void {
    this.show(message, 8000, 'server-snack-error');
  }

  /** Confirm something worked (already-translated text). */
  reportSuccess(message: string): void {
    this.show(message, 4000, 'server-snack-success');
  }

  private show(message: string, duration: number, panelClass: string): void {
    if (!message) { return; }
    this.snackBar.open(message, LanguagePipe.get('Dismiss', 'compressSpaces'),
      { duration: duration, panelClass: [panelClass] });
  }
}

